import { Injectable } from '@angular/core';
import {
  AcknowledgeOutdatedConnectionsGQL,
  GetAcademicsGQL,
  GetMatchedOrganizationsGQL,
  GetOrganizationGQL,
  GetPremiumPartnersGQL,
  OrganizationType,
  RevokePremiumPartnerConnectionGQL
} from '@cpd-app/generated';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  CurrentUserConnections,
  MatchedOrganizations,
  OrganizationModel,
  PremiumPartnerConnectionInput,
  PremiumPartnerModel,
  UpdateConnectionResponse,
  UserConnectionProps
} from '@cpd-app/core-module/models';
import { Apollo, gql } from 'apollo-angular';

/**
 * CONNECT.1. Request Info's own, ADD-ONLY save.
 *
 * `connectWithOrganizations` saves what it is given as the member's whole
 * selection, which is right for the Connect panel and was destructive from
 * Request Info: it removed every connection not in the drawer.
 *
 * A raw `gql` document rather than a generated operation, because codegen here
 * pulls its schema from the DEPLOYED gateway and cannot see a mutation that
 * has not shipped. FeatureEventService does the same for the same reason.
 */
export const REQUEST_INFO_FROM_SCHOOLS = gql`
  mutation RequestInfoFromSchools($academicIds: [ID!]!, $confirmAdult: Boolean) {
    requestInfoFromSchools(academicIds: $academicIds, confirmAdult: $confirmAdult) {
      connectedCount
    }
  }
`;

/*
 * CONNECT.1. The next three replace generated operations, for the same reason
 * as the one above: each carries a field or argument the deployed gateway does
 * not have yet (the named school list and the 18+ date on the state; the
 * confirmAdult argument on the two shares), so codegen cannot produce them.
 * Once CONNECT.1 is deployed everywhere these can move back into
 * partner-connect.graphql and be regenerated.
 */
export const GET_CONNECT_STATE = gql`
  query GetConnectOrganizationStateV2 {
    getConnectOrganizationState {
      academic
      employer
      nonprofit
      totalCount
      autoShareEnabled
      premiumPartnerIds
      hasOutdatedConnection
      adultConfirmedAt
      academicConnections {
        id
        name
        requestedAt
      }
    }
  }
`;

export const CONNECT_WITH_ORGANIZATIONS = gql`
  mutation ConnectWithOrganizationsV2(
    $academicIds: [ID!]
    $employerIds: [ID!]
    $nonprofitIds: [ID!]
    $autoShareEnabled: Boolean
    $confirmAdult: Boolean
  ) {
    connectWithOrganizations(
      academicIds: $academicIds
      employerIds: $employerIds
      nonprofitIds: $nonprofitIds
      autoShareEnabled: $autoShareEnabled
      confirmAdult: $confirmAdult
    ) {
      connectedCount
    }
  }
`;

export const CONNECT_WITH_PREMIUM_PARTNER = gql`
  mutation ConnectWithPremiumPartnerV2($input: PremiumPartnerConnectionInput!) {
    connectWithPremiumPartner(input: $input) {
      id
      consentedAt
      revokedAt
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class ConnectApiService {
  constructor(
    private readonly getMatchedOrganizationsGQL: GetMatchedOrganizationsGQL,
    private readonly getOrganizationGQL: GetOrganizationGQL,
    private readonly acknowledgeOutdatedConnectionsGQL: AcknowledgeOutdatedConnectionsGQL,
    private readonly getAcademicsGQL: GetAcademicsGQL,
    private readonly getPremiumPartnersGQL: GetPremiumPartnersGQL,
    private readonly revokePremiumPartnerConnectionSQL: RevokePremiumPartnerConnectionGQL,
    private readonly apollo: Apollo
  ) {}

  /** CONNECT.1. Resolves to how many schools the request covered. */
  public requestInfoFromSchools(
    schoolIds: number[],
    confirmAdult = false
  ): Observable<number> {
    return this.apollo
      .mutate<{ requestInfoFromSchools: { connectedCount: number } }>({
        mutation: REQUEST_INFO_FROM_SCHOOLS,
        variables: { academicIds: schoolIds.map(String), confirmAdult }
      })
      .pipe(
        map((result) => result?.data?.requestInfoFromSchools?.connectedCount ?? 0)
      );
  }

  public getMatchedOrganization(): Observable<MatchedOrganizations> {
    return this.getMatchedOrganizationsGQL.fetch().pipe(
      map((result) => {
        return result?.data.getMatchedOrganizations;
      })
    );
  }

  public getOrganization(
    type: string,
    query?: string
  ): Observable<OrganizationModel[]> {
    if (type === OrganizationType.Academic) {
      return this.getAcademicsGQL.fetch({ query }).pipe(
        map((result) => {
          return result?.data.getOrganizations;
        })
      );
    }

    return this.getOrganizationGQL
      .fetch({
        query,
        type: type as OrganizationType
      })
      .pipe(
        map((result) => {
          return result?.data.getOrganizations;
        })
      );
  }

  public getCurrentConnectionState(): Observable<CurrentUserConnections> {
    return this.apollo
      .query<{ getConnectOrganizationState: CurrentUserConnections }>({
        query: GET_CONNECT_STATE,
        fetchPolicy: 'no-cache'
      })
      .pipe(map((result) => result?.data?.getConnectOrganizationState));
  }

  public updateUserConnection(
    connection: UserConnectionProps
  ): Observable<UpdateConnectionResponse> {
    const connectProps = {
      academicIds: connection.academic,
      employerIds: connection.employer,
      nonprofitIds: connection.nonprofit,
      autoShareEnabled: connection.autoShareEnabled,
      confirmAdult: connection.confirmAdult ?? false
    };
    return this.apollo
      .mutate<{ connectWithOrganizations: UpdateConnectionResponse }>({
        mutation: CONNECT_WITH_ORGANIZATIONS,
        variables: connectProps
      })
      .pipe(map((result) => result?.data?.connectWithOrganizations));
  }

  public acknowledgeOutdatedConnection(): Observable<{
    autoShareEnabled: boolean;
  }> {
    return this.acknowledgeOutdatedConnectionsGQL
      .mutate()
      .pipe(map((result) => result.data.acknowledgeOutdatedConnections));
  }

  public getPremiumPartners(): Observable<PremiumPartnerModel[]> {
    return this.getPremiumPartnersGQL
      .fetch()
      .pipe(map((result) => result?.data.getPremiumPartners));
  }

  public connectWithPremiumPartner(
    connectPremiumProps: PremiumPartnerConnectionInput
  ) {
    return this.apollo
      .mutate<{
        connectWithPremiumPartner: { id: string; consentedAt: string; revokedAt: string | null };
      }>({
        mutation: CONNECT_WITH_PREMIUM_PARTNER,
        variables: { input: connectPremiumProps }
      })
      .pipe(map((result) => result?.data?.connectWithPremiumPartner));
  }

  public revokePremiumPartnerConnection(premiumPartnerId: string) {
    return this.revokePremiumPartnerConnectionSQL
      .mutate({ premiumPartnerId })
      .pipe(map((result) => result.data.revokePremiumPartnerConnection));
  }
}
